/**
 * The document the Mac menu bar is currently talking to.
 *
 * Menu items cannot close over React state from the editor page: the menu is
 * installed once and lives for the life of the process. Handlers are stored
 * here and read at click time, so Save/Print/Export always hit the open sermon
 * rather than the one that happened to be on screen when the menu was built.
 */

export type DesktopExportKind =
  | "manuscript-md"
  | "manuscript-print"
  | "source-md"
  | "pptx"
  | "images"
  | "slides-pdf"
  | "marp";

export interface DesktopDocumentSession {
  id: number;
  title: string;
  dirty: boolean;
  hasSlides: boolean;
  save: () => void;
  print: () => void;
  close: () => void;
  getContent: () => string;
  exportKind: (kind: DesktopExportKind) => void;
}

let session: DesktopDocumentSession | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function getDesktopDocument(): DesktopDocumentSession | null {
  return session;
}

export function setDesktopDocument(next: DesktopDocumentSession | null) {
  const previous = session;
  session = next;
  const sameDocument =
    (previous?.id ?? null) === (next?.id ?? null) &&
    Boolean(previous?.hasSlides) === Boolean(next?.hasSlides);
  if (!sameDocument) notify();
}

export function subscribeDesktopDocument(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
